import { useCallback, useEffect, useState } from 'react';
import { money } from '../lib/format';
import { ApiFailure, api } from '../lib/api';
import type { DriverRow } from '../lib/types';
import { X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

interface Period {
  id: string;
  driver: { id: string; name: string; driverNo: string };
  periodStart: string;
  periodEnd: string;
  status: string;
  statusLabel: string;
  orderCount: number;
  grossAmount: string;
  deduction: string;
  netAmount: string;
  currency: string;
  confirmedAt: string | null;
  paidAt: string | null;
}

interface Item {
  id: string;
  type: string;
  amount: string;
  note: string | null;
  order: { id: string; orderNo: string; receiverName: string; deliveredAt: string | null } | null;
}

interface Detail extends Period {
  items: Item[];
}

const TABS: Array<{ key: string; label: string; status?: string }> = [
  { key: 'draft', label: '待核对', status: 'DRAFT' },
  { key: 'confirmed', label: '待付款', status: 'CONFIRMED' },
  { key: 'paid', label: '已付款', status: 'PAID' },
  { key: 'all', label: '全部' },
];

const TYPE_LABEL: Record<string, string> = {
  DELIVERY: '配送费',
  BONUS: '奖励',
  PENALTY: '罚款',
  ADJUSTMENT: '调整',
};

const STATUS_STYLE: Record<string, string> = {
  DRAFT: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400',
  CONFIRMED: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400',
  PAID: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400',
};

/**
 * 把整数最小单位变成人看的金额。
 *
 * ★ 全程走字符串和整数,不碰浮点。
 *   `Number(minor) / 100` 在大额上会掉精度 —— 而这是要打给人的钱。
 */

/**
 * 司机结算。
 *
 * ★ 这一屏的每一个数字都要能**当着司机的面算一遍**。
 *   出一次错就再也没有信任度了 —— 所以明细必须列到每一单,
 *   而不是只给一个总额。
 */
export function Settlements({
  permissions,
  drivers,
}: {
  permissions: string[];
  drivers: DriverRow[];
}) {
  /** 结到哪天(含)。留空 = 到昨天为止,也就是原来的行为 */
  const [until, setUntil] = useState('');
  /** 从哪天开始(含)。留空 = 有多早算多早,也就是原来的行为 */
  const [from, setFrom] = useState('');
  /** 只结这一个司机。留空 = 全部 */
  const [onlyDriver, setOnlyDriver] = useState('');
  /*
   * ★ 日期选择器的上限是**昨天**,不是今天。
   *   今天的单还在跑 —— 结进去的话,司机下午送的那几单会落在
   *   一张已确认的单子外面,而那时候单子已经不能改了。
   *   ★ 这道线**只在这儿**。服务端不拦 —— 理由写在 settlement.service
   *     的 generate 里(拦了的话验「含 X 日」语义的那条用例没法写)。
   *     所以它是一个给人用的护栏,不是一道安全闸。
   */
  const yesterday = new Date(Date.now() - 24 * 3600_000).toISOString().slice(0, 10);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState('draft');
  /** 每个状态各有几张 —— 空标签旁边要能看到别处有货 */
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [rows, setRows] = useState<Period[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = permissions.includes('settlement:manage');
  const canPay = permissions.includes('settlement:pay');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = TABS.find((t) => t.key === tab)?.status;
      const q = new URLSearchParams({ limit: '100' });
      if (status) q.set('status', status);
      const [list, all] = await Promise.all([
        api<Period[]>(`/settlements?${q.toString()}`),
        /*
         * ★ 顺手把各状态各有几张算出来。
         *
         *   用户报的:「打开开关后还是没有确认功能」——真相是那张单
         *   在「待付款」里,而这一页默认停在「待核对」,那一栏空的,
         *   屏幕上只有一句「这里还没有结算单」。
         *   一个空标签旁边就是有东西的标签,却什么都不说 ——
         *   人得出的结论是「功能没有」,而不是「我站错了栏」。
         */
        api<Period[]>('/settlements?limit=100'),
      ]);
      setCounts(
        all.reduce<Record<string, number>>((acc, r) => {
          acc[r.status] = (acc[r.status] ?? 0) + 1;
          return acc;
        }, {}),
      );
      setRows(list);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到结算单');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // 读不到就当全关 —— 宁可少给一个按钮,不能多给一个能扣钱的按钮
    void api<Array<{ key: string; enabled: boolean }>>('/features')
      .then((f) => setFeatures(Object.fromEntries(f.map((x) => [x.key, x.enabled]))))
      .catch(() => setFeatures({}));
  }, []);

  async function generate() {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api<{
        periods: Period[];
        skipped: Array<{ driverNo: string; reason: string }>;
      }>('/settlements/generate', {
        method: 'POST',
        body: {
          /*
           * ★ 两个都不填就是原来的行为:全部司机、结到昨天为止。
           *   用户原话:「如果都不选择就是默认现在的方式」——
           *   所以这里传的是 undefined 而不是某个「今天」的默认值:
           *   后端那句注释写着「不传就是到昨天为止」,
           *   前端再算一遍只会多一处会和它算不一样的地方。
           */
          ...(from ? { from } : {}),
          ...(until ? { until } : {}),
          ...(onlyDriver ? { driverId: onlyDriver } : {}),
        },
      });
      // 生成了几张、跳过了几个,都要说 —— 只说「已生成」的话,
      // 一个司机被漏掉了没人会发现
      setError(
        r.periods.length === 0
          ? '没有可结算的单(已送达且还没入过账的单为 0)'
          : `生成 ${r.periods.length} 张结算单${r.skipped.length ? `,${r.skipped.length} 位司机没有待结算的单` : ''}`,
      );
      setTab('draft');
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '生成失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">
          司机结算
        </h1>
        {canManage && (
          <div className="flex flex-wrap items-end gap-2">
            {/*
              ★ 从哪天到哪天、只结谁 —— 三个都可以留空,留空就是原来的行为。
                用户原话:「管理端结算的时候可以选择日期范围和司机进行结算,
                如果都不选择就是默认现在的方式」。

              ★ 起点这一头原先**故意没有**输入框,理由是「填早了会把已经
                结过的单再结一遍」。那条理由不成立 —— 生成时永远只取
                **没有结算过的**单(结算单作废之后条目级联删掉,那批单
                会重新回到待结算里),重复结算另有库里那条部分唯一索引兜底。
                所以填早了最多是白筛一道,不会多付一分钱。

              ★ 「填晚了会漏掉中间那几天」这一条**仍然成立**,但它是可逆的:
                漏掉的单还留在待结算里,下次不填日期跑一遍就补上了。
                下面那段说明里把这件事讲清楚,而不是靠不给这个框来回避。
            */}
            <label className="text-xs text-slate-500">
              <span className="mb-1 block">从哪天(含)</span>
              <input
                type="date"
                aria-label="从哪天"
                value={from}
                max={yesterday}
                onChange={(e) => setFrom(e.target.value)}
                className="h-10 rounded-lg border border-slate-300 px-2 text-sm
                           dark:border-slate-600 dark:bg-slate-800"
              />
            </label>
            <label className="text-xs text-slate-500">
              <span className="mb-1 block">结到哪天(含)</span>
              <input
                type="date"
                aria-label="结到哪天"
                value={until}
                max={yesterday}
                onChange={(e) => setUntil(e.target.value)}
                className="h-10 rounded-lg border border-slate-300 px-2 text-sm
                           dark:border-slate-600 dark:bg-slate-800"
              />
            </label>
            <label className="text-xs text-slate-500">
              <span className="mb-1 block">只结这个司机</span>
              <select
                aria-label="只结这个司机"
                value={onlyDriver}
                onChange={(e) => setOnlyDriver(e.target.value)}
                className="h-10 rounded-lg border border-slate-300 px-2 text-sm
                           dark:border-slate-600 dark:bg-slate-800"
              >
                <option value="">全部司机</option>
                {drivers
                  .filter((d) => d.status === 'ACTIVE')
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}({d.driverNo})
                    </option>
                  ))}
              </select>
            </label>
            <button
              onClick={() => void generate()}
              disabled={loading}
              className="h-10 px-4 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-40"
            >
              {loading ? '处理中…' : '生成结算单'}
            </button>
          </div>
        )}
      </div>
      {/* 说清楚结到哪天 —— 不说的话,司机问「今天的呢」时没人答得上来 */}
      <p className="mb-4 text-sm text-slate-500">
        日期都不选就<strong>结算到昨天为止</strong>、把所有没结算过的单收进来,不选司机就是全部人。
        {/*
          ★ 「不管选哪一段,进来的都只有没结算过的单」这句必须说 ——
            用户原话就是这一条。不说的话,运营不敢往早了填:
            他会怕把上个月已经结过的单再结一遍。
        */}
        <br />
        不管选哪一段,<strong>只有没结算过的单会被收进来</strong>
        (结算单作废之后,那批单会重新回到待结算里)—— 所以开始日期填早了 不会重复结算,最多是白筛一道。
        {/*
          ★ 「最多到昨天」不只是 max 属性挡一下,要把**为什么**写出来 ——
            否则运营只会觉得这个控件坏了。
        */}
        <br />
        最晚只能结到昨天:今天的单还在跑,结进去的话司机下午送的那几单
        会落在一张已确认的单子外面。开始日期填晚了会跳过中间那几天, 但那些单还留着 ——
        下次不选日期跑一遍就补上了。
      </p>

      <div className="flex gap-1 mb-4 border-b border-slate-200 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-brand-600 text-brand-600 dark:text-brand-500 font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
            {t.status && (counts[t.status] ?? 0) > 0 && (
              <span className="ml-1 tabular-nums">{counts[t.status]}</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div
          className="mb-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200
                        dark:border-slate-700 px-4 py-2 text-sm text-slate-700 dark:text-slate-300"
        >
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          <p>这一栏没有结算单。</p>
          {/*
            ★ 别处有的话就直接说,并且能一键跳过去。
              「这里没有」和「一张都没有」是两件事,而屏幕上原来只写了前一句。
          */}
          {TABS.filter((t) => t.status && t.key !== tab && (counts[t.status] ?? 0) > 0).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="mt-2 block w-full text-brand-600 hover:underline dark:text-brand-500"
            >
              「{t.label}」里有 {counts[t.status!]} 张 —— 点这里看
            </button>
          ))}
        </div>
      ) : (
        <div
          className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                        bg-white dark:bg-slate-800"
        >
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-3 text-left font-medium">司机</th>
                <th className="p-3 text-left font-medium">周期</th>
                <th className="p-3 text-left font-medium">状态</th>
                <th className="p-3 text-right font-medium">单数</th>
                <th className="p-3 text-right font-medium">应付</th>
                <th className="p-3 text-right font-medium">扣款</th>
                <th className="p-3 text-right font-medium">实付</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => void openDetail(p.id, setDetail, setError)}
                  className="border-t border-slate-100 dark:border-slate-700/50 cursor-pointer
                             hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <td className="p-3">
                    <div className="text-slate-900 dark:text-slate-100">{p.driver.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{p.driver.driverNo}</div>
                  </td>
                  <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                    {p.periodStart.slice(0, 10)} ~ {p.periodEnd.slice(0, 10)}
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        STATUS_STYLE[p.status] ?? 'bg-slate-100 dark:bg-slate-700'
                      }`}
                    >
                      {p.statusLabel}
                    </span>
                  </td>
                  <td className="p-3 text-right tabular-nums text-slate-600 dark:text-slate-400">
                    {p.orderCount}
                  </td>
                  <td className="p-3 text-right tabular-nums text-slate-600 dark:text-slate-400">
                    {money(p.grossAmount, p.currency)}
                  </td>
                  <td className="p-3 text-right tabular-nums text-red-600 dark:text-red-400">
                    {p.deduction === '0'
                      ? '—'
                      : `-${money(p.deduction, p.currency).slice(p.currency.length + 1)}`}
                  </td>
                  <td className="p-3 text-right tabular-nums font-medium text-slate-900 dark:text-slate-100">
                    {money(p.netAmount, p.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <PeriodDialog
          period={detail}
          canManage={canManage}
          /*
           * ★ 「能不能点那个按钮」= 有 settlement:pay,**或者**
           *   租户开了「确认和打款可以同一个人做」。
           *   后者是给一个人的车队用的 —— 原来他们谁也点不了。
           */
          canPay={canPay || (features['settlementSelfPay'] === true && canManage)}
          features={features}
          onClose={() => setDetail(null)}
          onChanged={async () => {
            await load();
            await openDetail(detail.id, setDetail, setError);
          }}
        />
      )}
    </div>
  );
}

async function openDetail(
  id: string,
  setDetail: (d: Detail) => void,
  setError: (e: string) => void,
): Promise<void> {
  try {
    setDetail(await api<Detail>(`/settlements/${id}`));
  } catch (e) {
    setError(e instanceof ApiFailure ? e.message : '读不到结算单详情');
  }
}

function PeriodDialog({
  period,
  canManage,
  canPay,
  features,
  onClose,
  onChanged,
}: {
  period: Detail;
  canManage: boolean;
  canPay: boolean;
  features: Record<string, boolean>;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  /*
   * ★ 能加哪几类调整由功能开关决定。
   *   扣款默认是关的 —— 从司机工资里扣钱这件事,应该是租户明确打开的,
   *   而不是装好就有。
   *   `ADJUSTMENT` 不设开关:它是兜底手段,关掉就没法纠错了。
   */
  const types = [
    ...(features['settlementPenalty'] ? [{ value: 'PENALTY', label: '罚款(扣钱)' }] : []),
    ...(features['settlementBonus'] ? [{ value: 'BONUS', label: '奖励(加钱)' }] : []),
    { value: 'ADJUSTMENT', label: '调整' },
  ];
  const [form, setForm] = useState({ type: types[0]?.value ?? 'ADJUSTMENT', amount: '', note: '' });

  const draft = period.status === 'DRAFT';

  /** 正在作废 —— 要先写一句为什么 */
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  /**
   * 跑一个结算单上的动作。
   *
   * ★ 回**成不成功**,不是回 void。
   *   调用方要据此决定「要不要把面板关掉」——
   *   失败了还把面板关掉的话,人填的原因没了、错误也被盖住,
   *   他只知道「点了一下,好像什么都没发生」。
   */
  async function act(path: string, body: unknown = {}): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await api(`/settlements/${period.id}/${path}`, { method: 'POST', body });
      await onChanged();
      return true;
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '操作失败');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addItem() {
    const yuan = Number(form.amount);
    if (!Number.isFinite(yuan) || yuan === 0) {
      setError('请填一个不为 0 的金额');
      return;
    }
    /*
     * ★ 界面上填的是「元」,接口收的是「分」。
     *   罚款自动取负 —— 但**提示语必须写出来**:
     *   让人自己填负号的话,总有一天会有人把罚款填成正数,
     *   而系统会当成奖励发出去。
     */
    const minor = Math.round(Math.abs(yuan) * 100) * (form.type === 'PENALTY' ? -1 : 1);
    // ★ 同理:加失败了别把面板关掉 —— 他填的金额和备注还在里面
    const ok = await act('items', { type: form.type, amount: minor, note: form.note || undefined });
    if (!ok) return;
    setAdding(false);
    setForm({ type: types[0]?.value ?? 'ADJUSTMENT', amount: '', note: '' });
  }

  async function removeItem(itemId: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/settlements/${period.id}/items/${itemId}`, { method: 'DELETE' });
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '删不掉');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="结算单"
        className="w-full max-w-3xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {period.driver.name} · {period.periodStart.slice(0, 10)} ~{' '}
            {period.periodEnd.slice(0, 10)}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>

        {error && (
          <div
            className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <span
            className={`text-xs px-1.5 py-0.5 rounded ${
              STATUS_STYLE[period.status] ?? 'bg-slate-100 dark:bg-slate-700'
            }`}
          >
            {period.statusLabel}
          </span>
          <span className="text-sm text-slate-500">{period.orderCount} 单</span>
          <span
            aria-label="实付金额"
            className="text-lg font-semibold text-slate-900 dark:text-slate-100 tabular-nums"
          >
            {money(period.netAmount, period.currency)}
          </span>
        </div>

        {/* ★ 明细列到每一单。只给总额的话,司机没法核对 ——
               而钱的数字必须可验算,出一次错就再也没有信任度 */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto mb-3">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-2 text-left font-medium">类型</th>
                <th className="p-2 text-left font-medium">订单 / 说明</th>
                <th className="p-2 text-right font-medium">金额</th>
                {draft && canManage && <th className="p-2 w-10" />}
              </tr>
            </thead>
            <tbody>
              {period.items.map((i) => (
                <tr key={i.id} className="border-t border-slate-100 dark:border-slate-700/50">
                  <td className="p-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                    {TYPE_LABEL[i.type] ?? i.type}
                  </td>
                  <td className="p-2 text-slate-700 dark:text-slate-300">
                    {i.order ? (
                      <>
                        <span className="font-mono text-xs">{i.order.orderNo}</span>
                        <span className="text-slate-400"> · {i.order.receiverName}</span>
                      </>
                    ) : (
                      <span className="text-slate-500">{i.note ?? '—'}</span>
                    )}
                    {i.order && i.note && <div className="text-xs text-slate-400">{i.note}</div>}
                  </td>
                  <td
                    className={`p-2 text-right tabular-nums ${
                      i.amount.startsWith('-')
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {money(i.amount, period.currency)}
                  </td>
                  {draft && canManage && (
                    <td className="p-2 text-right">
                      {/* 配送费不给删 —— 后端也拦着,这里只是别让按钮说谎 */}
                      {i.type !== 'DELIVERY' && (
                        <button
                          aria-label={`删除 ${TYPE_LABEL[i.type] ?? i.type}`}
                          onClick={() => void removeItem(i.id)}
                          className="text-slate-400"
                        >
                          <InlineIcon icon={X} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {draft && canManage && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="mb-3 text-sm text-brand-600 dark:text-brand-500"
          >
            + 加一条调整
          </button>
        )}

        {draft && canManage && adding && (
          <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="flex gap-2">
              <select
                aria-label="调整类型"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              >
                {types.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <input
                aria-label="金额"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputMode="decimal"
                placeholder="金额"
                className="w-28 h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              />
              <input
                aria-label="原因"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="原因(司机看得到)"
                className="flex-1 h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              />
            </div>
            {/* ★ 说清正负,别让人自己填负号 */}
            <p className="text-xs text-slate-400">
              {form.type === 'PENALTY'
                ? '填正数即可,系统按扣款处理。原因会显示在司机的结算单上。'
                : '填正数,按加钱处理。原因会显示在司机的结算单上。'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setAdding(false)}
                className="px-3 h-9 rounded-lg border border-slate-300 dark:border-slate-600 text-sm"
              >
                取消
              </button>
              <button
                onClick={() => void addItem()}
                disabled={busy}
                className="px-3 h-9 rounded-lg bg-brand-600 text-white text-sm disabled:opacity-40"
              >
                加上
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            关闭
          </button>
          {draft && canManage && (
            <button
              onClick={() => void act('confirm')}
              disabled={busy}
              className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
            >
              确认金额
            </button>
          )}
          {/*
            ★ 生成错了要能退回去(用户原话:「万一弄错了取消,然后重新生成」)。
              已付款的不给作废 —— 钱已经出去了,删掉它等于把一笔付款从账上抹掉。
          */}
          {period.status !== 'PAID' && canManage && (
            <button
              onClick={() => setCancelling(true)}
              disabled={busy}
              className="h-10 rounded-lg border border-red-300 px-3 text-red-600
                         disabled:opacity-40 dark:border-red-900"
            >
              作废
            </button>
          )}
          {period.status === 'CONFIRMED' && canPay && (
            <button
              onClick={() => void act('pay')}
              disabled={busy}
              className="flex-1 h-10 rounded-lg bg-green-600 text-white font-medium disabled:opacity-40"
            >
              标记已付款
            </button>
          )}
        </div>

        {cancelling && (
          <div
            className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 dark:border-red-900
                          dark:bg-red-950/30"
          >
            <p className="text-sm text-red-800 dark:text-red-300">
              作废之后这张单会被删掉,里面那 {period.orderCount} 单会**回到待结算**,
              可以重新生成。已经付过款的单不能作废。
            </p>
            {/*
              ★ 必须写原因:这一行会被真的删掉,而「为什么作废了那一张」
                这个问题通常是在对账对不上的时候才被问出来 ——
                那时候唯一能回答的是审计日志。
            */}
            <input
              aria-label="作废原因"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="为什么作废?比如:周期选错了"
              className="mt-2 w-full rounded-lg border border-red-300 px-3 py-2 text-sm
                         dark:border-red-900 dark:bg-slate-900"
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => {
                  setCancelling(false);
                  setCancelReason('');
                }}
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm text-slate-600
                           dark:border-slate-600"
              >
                算了
              </button>
              <button
                /*
                  ★ 成功之后把这个面板收起来。
                    原来只调了 act:作废成功、列表也刷新了,而这块
                    「填原因 + 确认作废」还开着 —— 人会以为没生效,
                    再点一次(第二次会报「这张单已经不在了」)。
                */
                onClick={() =>
                  void act('cancel', { reason: cancelReason.trim() }).then((ok) => {
                    if (!ok) return;
                    setCancelling(false);
                    setCancelReason('');
                  })
                }
                disabled={busy || cancelReason.trim().length < 2}
                className="h-9 rounded-lg bg-red-600 px-3 text-sm font-medium text-white
                           disabled:opacity-40"
              >
                确认作废
              </button>
            </div>
          </div>
        )}

        {draft && canManage && (
          // 确认之后就改不动了 —— 按钮旁边说清楚,别让人事后才知道
          <p className="mt-2 text-xs text-slate-400">
            确认之后金额定死,不能再加减 —— 要纠正只能挂到下一期。
          </p>
        )}
        {period.status === 'CONFIRMED' && !canPay && (
          /*
           * ★ 点不了的时候要告诉他**怎么才能点**,而不是只说一句
           *   「需要财务账号」——「但是我们现在没有这些东西」正是
           *   用户的原话。两条路都写出来:再开一个账号,
           *   或者明确地把这两件事合成一个人做。
           */
          <p className="mt-2 text-xs text-slate-400">
            金额已确认。付款默认要单独的财务账号 —— 核对和放款是分开的两件事。
            <br />
            只有一个人的话:去「设置 → 功能开关」打开
            <b>「确认和打款可以同一个人做」</b>,或者在「设置 → 用户」里 建一个带财务角色的账号。
          </p>
        )}
      </div>
    </div>
  );
}
